import type { InferType } from 'yup';
import { array, boolean, number, object, string } from 'yup';

import { cartContentsResponseSchema } from '~/utils/api/cart';
import { pickUpPointSchema } from '~/utils/api/common';
import { couponOptions } from '~/utils/api/plugins/coupon';
import { addressSchema, userSchema } from '~/utils/api/user';

const availableDeliveryTimeSchema = object()
  .shape({
    value: string().required(),
    isSelected: boolean().required(),
  })
  .required();

const availableDeliveryDateSchema = object()
  .shape({
    date: string().required(),
    isSelected: boolean().required(),
    timeIntervals: array().ensure().of(availableDeliveryTimeSchema).required(),
  })
  .required();

const orderDeliverySchema = object()
  .shape({
    isReady: boolean().required(),
    addresses: array().ensure().of(addressSchema).required(),
    variants: array()
      .ensure()
      .of(
        object()
          .shape({
            name: string().required(),
            type: string().required(),
          })
          .required(),
      )
      .required(),
    current: object()
      .shape({
        addressId: number().nullable(),
        pvz: pickUpPointSchema.nullable(),
        availableDates: array().of(availableDeliveryDateSchema).nullable(),
        calculatedDeliveryDate: object()
          .shape({
            dateFrom: string().required(),
            dateTo: string().required(),
          })
          .required()
          .nullable(),
        selectedDatetime: string().nullable(),
        type: string().required(),
      })
      .nullable(),
  })
  .required();

const orderPaymentSchema = object()
  .shape({
    isReady: boolean().required(),
    current: object().shape({ type: string().required() }).nullable(),
    variants: array()
      .ensure()
      .of(
        object()
          .shape({
            name: string().required(),
            type: string().required(),
          })
          .required(),
      )
      .required(),
  })
  .required();

const orderSummarySchema = object()
  .shape({
    basketPrice: number().required(),
    deliveryPrice: number().required(),
    discountPrice: number().required(),
    total: number().required(),
  })
  .required();

const orderOtherReceiverSchema = object()
  .shape({
    lastName: string().required(),
    name: string().required(),
    phone: string().required(),
  })
  .required()
  .nullable();

const currentOrderResponseSchema = object()
  .shape({
    isReady: boolean().required(),
    basket: cartContentsResponseSchema,
    delivery: orderDeliverySchema,
    summary: orderSummarySchema,
    payment: orderPaymentSchema,
    user: userSchema.shape({
      isReady: boolean().required(),
      otherReceiver: orderOtherReceiverSchema,
    }),
  })
  .required();

const activateOrderResponseSchema = object()
  .shape({
    id: number().required(),
    message: string().required(),
  })
  .required();

const setRecipientBodySchema = object()
  .shape({
    name: string().required(),
    lastName: string().required(),
    phone: string().required(),
  })
  .required();

const selectDeliveryBodySchema = object()
  .shape({
    type: string().required(),
    pvzId: number().optional(),
    addressId: number().optional(),
  })
  .required();

const selectDeliveryDateTimeBodySchema = object()
  .shape({
    date: string().required(),
    timeInterval: string().required(),
  })
  .required();

const couponResponseSchema = object().shape({ name: string().required(), value: string().required() }).required();

const couponBodySchema = object().shape({ coupon: string().required() }).required();

const setPaymentBodySchema = object().shape({ type: string().required() }).required();

const getPaymentTokenParamsSchema = object().shape({ orderId: number().required().positive() }).required();

const getPaymentTokenResponseSchema = object().shape({ token: string().required() }).required();

export type OrderInfoType = InferType<typeof currentOrderResponseSchema>;

export type OrderOtherRecipientDataType = InferType<typeof orderOtherReceiverSchema>;

export type OrderDeliveryDataType = InferType<typeof orderDeliverySchema>;

export type OrderPaymentDataType = InferType<typeof orderPaymentSchema>;

export type OrderSummaryDataType = InferType<typeof orderSummarySchema>;

export type AvailableDeliveryTimeType = InferType<typeof availableDeliveryTimeSchema>;

export type AvailableDeliveryDateType = InferType<typeof availableDeliveryDateSchema>;

export const OrderAPI = {
  current: StrictFetch.prepare({
    url: 'orders/current',
    schemas: { response: currentOrderResponseSchema },
    options: [couponOptions, { methodKey: 'order:current', selfInterrupted: true }],
  }),
  activate: StrictFetch.prepare({
    url: 'orders/current',
    method: HTTPMethod.POST,
    schemas: { response: activateOrderResponseSchema },
    options: couponOptions,
  }),
  setRecipient: StrictFetch.prepare({
    url: 'orders/current/receiver',
    method: HTTPMethod.POST,
    schemas: {
      body: setRecipientBodySchema,
      response: currentOrderResponseSchema,
    },
    options: couponOptions,
  }),
  deleteRecipient: StrictFetch.prepare({
    url: 'orders/current/receiver/clear',
    schemas: { response: currentOrderResponseSchema },
    options: couponOptions,
  }),
  selectDelivery: StrictFetch.prepare({
    url: 'orders/current/delivery',
    method: HTTPMethod.POST,
    schemas: {
      body: selectDeliveryBodySchema,
      response: currentOrderResponseSchema,
    },
    options: [couponOptions, { methodKey: 'order:selectDelivery', selfInterrupted: true }],
  }),
  selectDeliveryDateTime: StrictFetch.prepare({
    url: 'orders/date',
    method: HTTPMethod.POST,
    schemas: {
      body: selectDeliveryDateTimeBodySchema,
      response: currentOrderResponseSchema,
    },
    options: couponOptions,
  }),
  setCoupon: StrictFetch.prepare({
    url: 'orders/current/coupon',
    method: HTTPMethod.POST,
    schemas: { body: couponBodySchema, response: couponResponseSchema },
    options: couponOptions,
  }),
  deleteCoupon: StrictFetch.prepare({
    url: 'orders/current/coupon',
    method: HTTPMethod.DELETE,
    schemas: { body: couponBodySchema, response: couponResponseSchema },
    options: couponOptions,
  }),
  setPayment: StrictFetch.prepare({
    url: 'orders/current/payment',
    method: HTTPMethod.POST,
    schemas: {
      body: setPaymentBodySchema,
      response: currentOrderResponseSchema,
    },
    options: couponOptions,
  }),
  getPaymentToken: StrictFetch.prepare({
    url: ({ orderId }) => `orders/${orderId}/payment/token`,
    schemas: {
      params: getPaymentTokenParamsSchema,
      response: getPaymentTokenResponseSchema,
    },
    options: couponOptions,
  }),
};
